import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  Autocomplete,
  Card,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Avatar,
  FormControlLabel,
  Switch,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Share as ShareIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { DetailPageHeader, DetailTwoColumnGrid } from '../../components/common/DetailPageLayout';
import BenchShareDialog from './BenchShareDialog';
import AvailableCandidatesSnippet from './AvailableCandidatesSnippet';
import { benchTypeLabel, benchNameSx, canShareBench } from './benchSalesPermissions';

const BenchDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const [bench, setBench] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [detailTab, setDetailTab] = useState(0);

  // Dialog states
  const [addCandidateDialog, setAddCandidateDialog] = useState(false);
  const [editCandidateDialog, setEditCandidateDialog] = useState({ open: false, candidate: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, candidate: null });
  const [availableCandidates, setAvailableCandidates] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [candidateForm, setCandidateForm] = useState({
    availability_status: 'available',
    expected_rate: '',
    visa_status: '',
    relocation: 'flexible',
    preferred_locations: '',
    notes: '',
    block_other_benches: false
  });
  const [savingBenchSettings, setSavingBenchSettings] = useState(false);
  const [visibilityEdit, setVisibilityEdit] = useState('private');
  const [opActiveEdit, setOpActiveEdit] = useState(true);

  useEffect(() => {
    fetchBenchDetails();
    fetchAvailableCandidates();
  }, [id]);

  const fetchBenchDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/bench-sales/${id}`);
      const b = response.data.data;
      setBench(b);
      if (b) {
        setVisibilityEdit(b.visibility || 'private');
        setOpActiveEdit(b.bench_operational_active !== false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch bench details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCandidates = async () => {
    try {
      const response = await api.get('/bench-sales/candidates/available', { params: { limit: 200 } });
      const data = response.data?.data || response.data;
      setAvailableCandidates(data?.candidates || data || []);
    } catch (err) {
      console.error('Failed to fetch available candidates', err);
    }
  };

  const saveBenchSettings = async () => {
    const payload = {};
    if (isAdmin) {
      payload.visibility = visibilityEdit;
      payload.bench_operational_active = opActiveEdit;
    } else if (bench?.created_by === user?.id) {
      payload.bench_operational_active = opActiveEdit;
    } else {
      return;
    }
    setSavingBenchSettings(true);
    try {
      await api.put(`/bench-sales/${id}`, payload);
      setSuccess('Bench settings saved');
      fetchBenchDetails();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save bench settings');
    } finally {
      setSavingBenchSettings(false);
    }
  };

  const handleAddCandidate = async () => {
    try {
      const { block_other_benches: blockOther, ...rest } = candidateForm;
      await api.post('/bench-sales/candidates', {
        bench_id: id,
        contact_id: selectedContact.id,
        ...rest,
        ...(isAdmin && blockOther ? { block_other_benches: true } : {}),
      });
      setSuccess('Candidate added successfully');
      setAddCandidateDialog(false);
      resetCandidateForm();
      fetchBenchDetails();
      fetchAvailableCandidates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add candidate');
    }
  };

  const handleUpdateCandidate = async () => {
    try {
      const { block_other_benches: _b, ...rest } = candidateForm;
      await api.put(`/bench-sales/candidates/${editCandidateDialog.candidate.id}`, rest);
      setSuccess('Candidate updated successfully');
      setEditCandidateDialog({ open: false, candidate: null });
      resetCandidateForm();
      fetchBenchDetails();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update candidate');
    }
  };

  const handleRemoveCandidate = async () => {
    try {
      await api.delete(`/bench-sales/candidates/${deleteDialog.candidate.id}`);
      setSuccess('Candidate removed successfully');
      setDeleteDialog({ open: false, candidate: null });
      fetchBenchDetails();
      fetchAvailableCandidates();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove candidate');
    }
  };

  const resetCandidateForm = () => {
    setSelectedContact(null);
    setCandidateForm({
      availability_status: 'available',
      expected_rate: '',
      visa_status: '',
      relocation: 'flexible',
      preferred_locations: '',
      notes: '',
      block_other_benches: false
    });
  };

  const openEditDialog = (candidate) => {
    setCandidateForm({
      availability_status: candidate.availability_status || 'available',
      expected_rate: candidate.expected_rate || '',
      visa_status: candidate.visa_status || '',
      relocation: candidate.relocation || 'flexible',
      preferred_locations: candidate.preferred_locations || '',
      notes: candidate.notes || '',
      block_other_benches: false,
    });
    setEditCandidateDialog({ open: true, candidate });
  };

  const candidates = bench?.candidates || [];
  const q = (searchTerm || '').trim().toLowerCase();
  const filteredCandidates = candidates.filter((bc) => {
    if (!q) return true;
    const fn = (bc.contact?.first_name || '').toLowerCase();
    const ln = (bc.contact?.last_name || '').toLowerCase();
    const em = (bc.contact?.email || '').toLowerCase();
    return fn.includes(q) || ln.includes(q) || em.includes(q);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'success';
      case 'interviewing': return 'warning';
      case 'placed': return 'primary';
      case 'unavailable': return 'error';
      default: return 'default';
    }
  };

  const canEditCandidates =
    isAdmin ||
    bench?.created_by === user?.id ||
    (bench?.bench_type === 'admin' && bench?.is_shared);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!bench) {
    return (
      <Alert severity="error">Bench not found</Alert>
    );
  }

  const benchInitial = (bench.name || 'B').trim().charAt(0).toUpperCase();

  return (
    <Box>
      <DetailPageHeader
        title="Bench Details"
        onBack={() => navigate(location.state?.listPath || '/bench-sales')}
        actions={
          <>
            {canEditCandidates ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddCandidateDialog(true)}>
                Add Candidate
              </Button>
            ) : null}
            {bench && canShareBench(user, isAdmin, bench) ? (
              <Button variant="outlined" sx={{ ml: 1 }} startIcon={<ShareIcon />} onClick={() => setShareOpen(true)}>
                Share
              </Button>
            ) : null}
          </>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <DetailTwoColumnGrid
        sidebar={
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2rem' }}>
                {benchInitial}
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                <Box component="span" sx={benchNameSx(bench.bench_type)}>
                  {bench.name}
                </Box>
              </Typography>
              {bench.bench_code && (
                <Chip label={bench.bench_code} size="small" sx={{ mt: 1, fontFamily: 'monospace', fontWeight: 600 }} />
              )}
              <Chip
                label={benchTypeLabel(bench.bench_type)}
                size="small"
                sx={{ mt: 1 }}
                color={
                  bench.bench_type === 'admin' ? 'primary' : bench.bench_type === 'manager' ? 'warning' : 'secondary'
                }
              />
              <Chip label={bench.status} size="small" sx={{ mt: 1, ml: 1 }} color="success" />
              <Divider sx={{ my: 3 }} />
              <Typography variant="body2" color="text.secondary">
                Candidates
              </Typography>
              <Typography variant="h6">{candidates.length}</Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, textAlign: 'left' }}>
                <PersonIcon color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Created by
                  </Typography>
                  <Typography>
                    {bench.creator?.first_name} {bench.creator?.last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {bench.creator?.email}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        }
      >
        <Card>
          <Tabs
            value={detailTab}
            onChange={(e, v) => setDetailTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="Overview" />
            <Tab label={`Candidates (${candidates.length})`} />
          </Tabs>
          <CardContent>
            {detailTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Typography>
                    <Chip
                      label={benchTypeLabel(bench.bench_type)}
                      size="small"
                      color={
                        bench.bench_type === 'admin'
                          ? 'primary'
                          : bench.bench_type === 'manager'
                            ? 'warning'
                            : 'secondary'
                      }
                    />
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Typography>
                    <Chip label={bench.status} size="small" color="success" />
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Shared
                  </Typography>
                  <Typography>
                    {bench.is_shared ? <Chip label="Yes" size="small" icon={<ShareIcon />} /> : 'No'}
                  </Typography>
                </Grid>
                {bench.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography>{bench.description}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Visibility
                  </Typography>
                  <Chip size="small" label={bench.visibility || 'private'} sx={{ mr: 1, mt: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Operational
                  </Typography>
                  <Chip
                    size="small"
                    label={bench.bench_operational_active !== false ? 'Active' : 'Inactive'}
                    color={bench.bench_operational_active !== false ? 'success' : 'default'}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                {(isAdmin || bench.created_by === user?.id) && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Adjust visibility & availability
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      {isAdmin ? (
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                          <InputLabel>Visibility</InputLabel>
                          <Select
                            label="Visibility"
                            value={visibilityEdit}
                            onChange={(e) => setVisibilityEdit(e.target.value)}
                          >
                            <MenuItem value="public">Public</MenuItem>
                            <MenuItem value="private">Private</MenuItem>
                            <MenuItem value="confidential">Confidential</MenuItem>
                          </Select>
                        </FormControl>
                      ) : null}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={opActiveEdit}
                            onChange={(e) => setOpActiveEdit(e.target.checked)}
                          />
                        }
                        label="Operational (active)"
                      />
                      <Button variant="outlined" size="small" onClick={saveBenchSettings} disabled={savingBenchSettings}>
                        {savingBenchSettings ? 'Saving…' : 'Save'}
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
            {detailTab === 1 && (
              <>
                <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
                  <TextField
                    placeholder="Search candidates..."
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ minWidth: 300 }}
                  />
                </Paper>
                <Paper variant="outlined">
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Candidate</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Expected Rate</TableCell>
                          <TableCell>Visa Status</TableCell>
                          <TableCell>Relocation</TableCell>
                          {canEditCandidates && <TableCell align="center">Actions</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCandidates.map((bc) => (
                          <TableRow key={bc.id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon color="primary" fontSize="small" />
                                {bc.contact?.first_name} {bc.contact?.last_name}
                              </Box>
                            </TableCell>
                            <TableCell>{bc.contact?.email}</TableCell>
                            <TableCell>{bc.contact?.phone}</TableCell>
                            <TableCell>
                              <Chip
                                label={bc.availability_status || 'Available'}
                                size="small"
                                color={getStatusColor(bc.availability_status)}
                              />
                            </TableCell>
                            <TableCell>
                              {bc.expected_rate && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <MoneyIcon fontSize="small" color="action" />${bc.expected_rate}/hr
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>{bc.visa_status || '-'}</TableCell>
                            <TableCell>
                              <Chip label={bc.relocation || 'Flexible'} size="small" variant="outlined" />
                            </TableCell>
                            {canEditCandidates && (
                              <TableCell align="center">
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => openEditDialog(bc)}>
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setDeleteDialog({ open: true, candidate: bc })}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                        {filteredCandidates.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={canEditCandidates ? 8 : 7} align="center">
                              <Typography color="text.secondary" py={4}>
                                No candidates in this bench
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </>
            )}
          </CardContent>
        </Card>
      </DetailTwoColumnGrid>

      <AvailableCandidatesSnippet />

      <BenchShareDialog
        open={shareOpen}
        benchId={id}
        listPath={location.state?.listPath || '/bench-sales'}
        onClose={(ok) => {
          setShareOpen(false);
          if (ok) setSuccess('Share email sent');
        }}
      />

      {/* Add Candidate Dialog */}
      <Dialog open={addCandidateDialog} onClose={() => setAddCandidateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Candidate to Bench</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={availableCandidates}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
              value={selectedContact}
              onChange={(e, newValue) => setSelectedContact(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Candidate" required />
              )}
            />
            <FormControl fullWidth>
              <InputLabel>Availability Status</InputLabel>
              <Select
                value={candidateForm.availability_status}
                label="Availability Status"
                onChange={(e) => setCandidateForm({ ...candidateForm, availability_status: e.target.value })}
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="interviewing">Interviewing</MenuItem>
                <MenuItem value="placed">Placed</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Expected Rate ($/hr)"
              type="number"
              value={candidateForm.expected_rate}
              onChange={(e) => setCandidateForm({ ...candidateForm, expected_rate: e.target.value })}
            />
            <TextField
              label="Visa Status"
              value={candidateForm.visa_status}
              onChange={(e) => setCandidateForm({ ...candidateForm, visa_status: e.target.value })}
              placeholder="e.g., H1B, Green Card, Citizen"
            />
            <FormControl fullWidth>
              <InputLabel>Relocation</InputLabel>
              <Select
                value={candidateForm.relocation}
                label="Relocation"
                onChange={(e) => setCandidateForm({ ...candidateForm, relocation: e.target.value })}
              >
                <MenuItem value="flexible">Flexible</MenuItem>
                <MenuItem value="open">Open to Relocation</MenuItem>
                <MenuItem value="no">Not Open to Relocation</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Preferred Locations"
              value={candidateForm.preferred_locations}
              onChange={(e) => setCandidateForm({ ...candidateForm, preferred_locations: e.target.value })}
              placeholder="e.g., New York, California, Remote"
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={candidateForm.notes}
              onChange={(e) => setCandidateForm({ ...candidateForm, notes: e.target.value })}
            />
            {isAdmin ? (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(candidateForm.block_other_benches)}
                    onChange={(e) =>
                      setCandidateForm({ ...candidateForm, block_other_benches: e.target.checked })
                    }
                  />
                }
                label="Block this candidate from being added to other benches (exclusive to this bench)"
              />
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddCandidateDialog(false); resetCandidateForm(); }}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleAddCandidate}
            disabled={!selectedContact}
          >
            Add Candidate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Candidate Dialog */}
      <Dialog 
        open={editCandidateDialog.open} 
        onClose={() => { setEditCandidateDialog({ open: false, candidate: null }); resetCandidateForm(); }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Edit Candidate</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Availability Status</InputLabel>
              <Select
                value={candidateForm.availability_status}
                label="Availability Status"
                onChange={(e) => setCandidateForm({ ...candidateForm, availability_status: e.target.value })}
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="interviewing">Interviewing</MenuItem>
                <MenuItem value="placed">Placed</MenuItem>
                <MenuItem value="unavailable">Unavailable</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Expected Rate ($/hr)"
              type="number"
              value={candidateForm.expected_rate}
              onChange={(e) => setCandidateForm({ ...candidateForm, expected_rate: e.target.value })}
            />
            <TextField
              label="Visa Status"
              value={candidateForm.visa_status}
              onChange={(e) => setCandidateForm({ ...candidateForm, visa_status: e.target.value })}
              placeholder="e.g., H1B, Green Card, Citizen"
            />
            <FormControl fullWidth>
              <InputLabel>Relocation</InputLabel>
              <Select
                value={candidateForm.relocation}
                label="Relocation"
                onChange={(e) => setCandidateForm({ ...candidateForm, relocation: e.target.value })}
              >
                <MenuItem value="flexible">Flexible</MenuItem>
                <MenuItem value="open">Open to Relocation</MenuItem>
                <MenuItem value="no">Not Open to Relocation</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Preferred Locations"
              value={candidateForm.preferred_locations}
              onChange={(e) => setCandidateForm({ ...candidateForm, preferred_locations: e.target.value })}
              placeholder="e.g., New York, California, Remote"
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={candidateForm.notes}
              onChange={(e) => setCandidateForm({ ...candidateForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditCandidateDialog({ open: false, candidate: null }); resetCandidateForm(); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleUpdateCandidate}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, candidate: null })}>
        <DialogTitle>Remove Candidate</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove "{deleteDialog.candidate?.contact?.first_name} {deleteDialog.candidate?.contact?.last_name}" from this bench?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, candidate: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRemoveCandidate}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BenchDetails;
